import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks, Pencil, RotateCcw, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const FIELDS = [
  { key: "total_points", label: "Punten", step: "0.1" },
  { key: "count_100plus", label: "100+" },
  { key: "count_140plus", label: "140+" },
  { key: "count_180", label: "180" },
  { key: "bavg", label: "TOT. AVG", step: "0.01" },
  { key: "matches_total", label: "Tot. Games" },
  { key: "matches_won", label: "Games Won" },
  { key: "legs_total", label: "Tot. Legs" },
  { key: "legs_won", label: "Legs Won" },
  { key: "tournaments_played", label: "Tot. tours" },
];

export const RankingEditor = () => {
  const [board, setBoard] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({});

  const load = () => {
    api.get("/leaderboard").then((r) => setBoard(r.data));
    api.get("/ranking-overrides").then((r) => setOverrides(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const overrideOf = (pid) => overrides.find((o) => o.player_id === pid);

  const openEdit = (p) => {
    const ov = overrideOf(p.player_id) || {};
    const f = {};
    FIELDS.forEach((fl) => (f[fl.key] = ov[fl.key] ?? ""));
    const legs = ov.best_legs || [];
    f.leg1 = legs[0] ?? "";
    f.leg2 = legs[1] ?? "";
    f.leg3 = legs[2] ?? "";
    setForm(f);
    setEditRow(p);
  };

  const save = async () => {
    const payload = {};
    FIELDS.forEach((fl) => {
      if (form[fl.key] !== "" && form[fl.key] !== null && form[fl.key] !== undefined) {
        payload[fl.key] = Number(form[fl.key]);
      }
    });
    const legs = [form.leg1, form.leg2, form.leg3].map(Number).filter((l) => l > 0);
    if (legs.length) payload.best_legs = legs;
    try {
      await api.put(`/ranking-overrides/${editRow.player_id}`, payload);
      toast.success("Ranking aangepast");
      setEditRow(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const clearOverride = async (pid) => {
    await api.delete(`/ranking-overrides/${pid}`);
    toast.success("Aanpassing gewist — weer automatisch");
    if (editRow) setEditRow(null);
    load();
  };

  const inputCls = "w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]";
  const labelCls = "text-[10px] uppercase tracking-wider text-[#525252] mb-1 block";

  return (
    <div className="mt-12" data-testid="ranking-editor">
      <div className="flex items-center gap-2 mb-2">
        <ListChecks className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="font-heading text-xl font-bold">Totale ranking aanpassen</h2>
      </div>
      <p className="text-sm text-[#525252] mb-4">
        Pas per speler de seizoensstatistieken handmatig aan. Een leeg veld = automatisch berekend uit de toernooiuitslagen.
      </p>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full" data-testid="ranking-editor-table">
          <thead>
            <tr className="bg-[#FDFBF7] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
              <th className="text-left py-3 px-4">Speler</th>
              <th className="text-right py-3 px-2">Punten</th>
              <th className="text-right py-3 px-2">180</th>
              <th className="text-right py-3 px-2">TOT. AVG</th>
              <th className="text-right py-3 px-2">Tot. tours</th>
              <th className="text-center py-3 px-2">Aangepast</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {board.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-sm text-[#525252]">Nog geen spelers.</td></tr>}
            {board.map((p) => {
              const ov = overrideOf(p.player_id);
              return (
                <tr key={p.player_id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FDFBF7]" data-testid={`ranking-editor-row-${p.player_id}`}>
                  <td className="py-2.5 px-4 font-medium text-sm">{p.name}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{p.total_points}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{p.count_180}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{p.bavg}</td>
                  <td className="py-2.5 px-2 text-right font-mono text-sm">{p.tournaments_played}</td>
                  <td className="py-2.5 px-2 text-center">
                    {ov ? <span className="text-[9px] uppercase tracking-wide bg-[#D4AF37] text-[#0A0A0A] px-1.5 py-0.5 font-bold">Handmatig</span> : <span className="text-[#A0A0A0]">—</span>}
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    {ov && (
                      <button onClick={() => clearOverride(p.player_id)} data-testid={`clear-override-${p.player_id}`} className="text-[#525252] hover:text-[#DC2626] mr-3" title="Terug naar automatisch">
                        <RotateCcw className="w-4 h-4 inline" />
                      </button>
                    )}
                    <button onClick={() => openEdit(p)} data-testid={`edit-ranking-${p.player_id}`} className="text-[#525252] hover:text-[#D4AF37]">
                      <Pencil className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent className="rounded-none border-2 border-[#0A0A0A] sm:max-w-2xl" data-testid="ranking-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">Ranking aanpassen — {editRow?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#525252] -mt-2">Laat een veld leeg om het automatisch te laten berekenen. Het huidige (berekende) getal staat als grijze placeholder.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
            {FIELDS.map((fl) => (
              <div key={fl.key}>
                <label className={labelCls}>{fl.label}</label>
                <input
                  type="number"
                  step={fl.step || "1"}
                  value={form[fl.key] ?? ""}
                  placeholder={editRow ? String(editRow[fl.key]) : ""}
                  onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })}
                  data-testid={`rank-field-${fl.key}`}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
          <div className="mt-1">
            <label className={labelCls}>Top 3 legs (darts, laagste is beste)</label>
            <div className="grid grid-cols-3 gap-3">
              <input type="number" value={form.leg1 ?? ""} onChange={(e) => setForm({ ...form, leg1: e.target.value })} data-testid="rank-field-leg1" placeholder={editRow?.best_legs?.[0] ?? ""} className={inputCls} />
              <input type="number" value={form.leg2 ?? ""} onChange={(e) => setForm({ ...form, leg2: e.target.value })} data-testid="rank-field-leg2" placeholder={editRow?.best_legs?.[1] ?? ""} className={inputCls} />
              <input type="number" value={form.leg3 ?? ""} onChange={(e) => setForm({ ...form, leg3: e.target.value })} data-testid="rank-field-leg3" placeholder={editRow?.best_legs?.[2] ?? ""} className={inputCls} />
            </div>
          </div>
          <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
            {editRow && overrideOf(editRow.player_id) && (
              <button onClick={() => clearOverride(editRow.player_id)} data-testid="ranking-clear-btn" className="inline-flex items-center justify-center gap-2 border border-[#0A0A0A] text-[#0A0A0A] py-3 px-4 font-semibold hover:bg-[#FDFBF7] transition-colors">
                <RotateCcw className="w-4 h-4" /> Terug naar automatisch
              </button>
            )}
            <button onClick={save} data-testid="ranking-save-btn" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#D4AF37] py-3 font-semibold hover:bg-[#1A1A1A] transition-colors">
              <Save className="w-4 h-4" /> Opslaan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
